'use client';

import Link from 'next/link';
import React from 'react';

import { Button } from '@/components/ui/button';
import { LinkAppearances } from '@/fields/link';
import type { Page } from '@/payload-types';
import { cn } from '@/utils/cn';
import { pushGTMEvent } from '@/utils/pushGTMEvent';

export type CMSLinkType = {
  ariaLabel?: string;
  appearance?: LinkAppearances;
  children?: React.ReactNode;
  className?: string;
  label?: string | null;
  newTab?: boolean | null;
  reference?: {
    value: Page | string | number;
  } | null;
  type?: 'custom' | 'reference' | null;
  url?: string | null;
  locale?: string;
  tracking?: {
    type: string;
    headline: string;
    cta: string;
  };
};

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    appearance = 'inline',
    ariaLabel,
    children,
    className,
    label,
    locale = 'da',
    newTab,
    reference,
    tracking,
    type,
    url,
  } = props;

  const href =
    (type === 'reference' && typeof reference?.value === 'object'
      ? typeof reference?.value?.url === 'object'
        ? reference?.value?.url?.[locale]
        : reference?.value?.url
      : typeof reference?.value === 'object'
        ? reference?.value?.url
        : undefined) || url;

  if (!href) return null;

  const newTabProps = newTab
    ? { rel: 'noopener noreferrer', target: '_blank' }
    : {};

  const handleClick = () => {
    if (tracking) {
      pushGTMEvent({
        event: 'component_click',
        component_cta: tracking.cta,
        component_headline: tracking.headline,
        component_type: tracking.type,
      });
    }
  };

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline') {
    return (
      <Link
        aria-label={ariaLabel}
        className={cn(className)}
        href={(href as string) || url || ''}
        onClick={handleClick}
        {...newTabProps}
      >
        {label && label}
        {children && children}
      </Link>
    );
  }

  return (
    <Button asChild className={className}>
      <Link
        aria-label={ariaLabel}
        className={cn(className, 'button', `button--${appearance}`, {
          'button--medium':
            appearance === 'level-3' || appearance === 'level-4',
        })}
        href={(href as string) || url || ''}
        onClick={handleClick}
        {...newTabProps}
      >
        {label && label}
        {children && children}
      </Link>
    </Button>
  );
};
